import React, { Fragment } from 'react';
import { always, isNil, min, take, times, pathOr } from 'ramda';
import { scaleLinear, scaleOrdinal } from '@vx/scale';
import { Circle, Line } from '@vx/shape';
import { Group } from '@vx/group';
import { ParentSize } from '@vx/responsive';
import { useTooltipInPortal } from '@vx/tooltip';
import { useIntl } from 'gatsby-plugin-intl';
import { toRadians } from '../../../utils';
import { TRANSPARENT, UI_COLORS, WHITE } from '../../../utils/colors';
import { renderWhenTrue } from '../../../utils/rendering';
import { SPIDER_DEFAULT } from '../configs/spider.config';
import { useChartTooltipContext } from '../../../store/ChartTooltipContext';
import { useGatsbyContext } from '../../../store/GatsbyContext';
import { renderOpenTooltip } from '../tooltipBasic/tooltipBasic.utils';
import { TooltipWithPortal } from '../tooltipWithPortal/tooltipWithPortal.component';
import { Header } from '../header';
import { isNoData, NoData } from '../noData';
import { Legend, Chart, Svg } from '../charts.styles';
import { DEFAULT_SIZE } from '../charts.constants';
import { SpiderGroup, SerieGroup, Container } from './spiderChart.styles';
import { TooltipTableDataCell, TooltipElementTable } from '../../tooltipElementTable/tooltipElementTable.styles';
import { toFixed } from '../../../utils/math';
import { TOOLTIP_MIN_WIDTH } from '../../tooltipElementTable/tooltipElementTable.constants';

const genPoints = (length, radius) => {
  const step = (Math.PI * 2) / length;
  const offset = toRadians(-180);

  return [...new Array(length)].map((_, i) => {
    const degree = -i * step + offset;
    const textRadius = radius + 10;
    const sinDegree = Math.sin(degree);
    const cosDegree = Math.cos(degree);

    return {
      x: radius * sinDegree,
      y: radius * cosDegree,
      xText: textRadius * sinDegree,
      yText: textRadius * cosDegree,
    };
  });
};

const renderLegend = ({ config, ...props }) =>
  renderWhenTrue(() => <Legend config={config} {...props} />)(config.legendVisibility);

const renderSerie = (
  config,
  axis,
  data,
  degreeScale,
  rScale,
  tooltip,
  handleHover,
  hoveredGroupIndex,
  hoveredPointIndex,
  isHoverActive
) => (serie, serieIndex) => {
  const { colors, serieStrokeWidth } = config;
  const points = data.reduce((list, item) => {
    const value = item[serie];

    if (isNil(value)) return list;

    const id = item[axis];
    const degree = degreeScale(id);

    list.push({
      x: rScale(value) * Math.sin(degree),
      y: rScale(value) * Math.cos(degree),
    });

    return list;
  }, []);

  const pointsDefinition = points.map(({ x, y }) => `${x},${y}`).join(' ');
  const color = colors[serieIndex];
  const radius = 4;
  const isGroupHovered = hoveredGroupIndex === serieIndex;

  return (
    <SerieGroup key={serieIndex} isHoverActive={isHoverActive} isGroupHovered={isGroupHovered}>
      <polygon
        points={pointsDefinition}
        fill="none"
        stroke={color}
        strokeWidth={serieStrokeWidth}
        shapeRendering="geometricPrecision"
      />
      {points.map(({ x, y }, pointIndex) => (
        <Circle
          key={pointIndex}
          fill={isGroupHovered && pointIndex === hoveredPointIndex ? color : TRANSPARENT}
          stroke={isGroupHovered && pointIndex === hoveredPointIndex ? WHITE : TRANSPARENT}
          strokeWidth={2}
          r={radius}
          transform={`translate(${x}, ${y})`}
          {...tooltip.bindHoverEvents(handleHover(serieIndex, pointIndex))}
        />
      ))}
    </SerieGroup>
  );
};

export const SpiderChart = ({
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  data: chartData = {},
  tabs,
  parser: customParser,
  config: customConfig = {},
}) => {
  if (customParser) customParser(chartData);

  const tooltip = useChartTooltipContext();
  const { containerRef } = useTooltipInPortal();
  const { piLabels } = useGatsbyContext();
  const intl = useIntl();
  chartData['title'] = intl.formatMessage({ id: chartData.title });
  const { title, subtitle, data, labels, series, axis } = chartData;

  if (isNoData(data, series)) return <NoData width={width} height={height} />;

  const config = {
    ...SPIDER_DEFAULT,
    ...customConfig,
  };

  const { rays, values, margin } = config;

  const degreeScale = scaleLinear({
    domain: [1, rays + 1],
    range: [Math.PI, -Math.PI],
  });

  const color = scaleOrdinal({
    domain: series,
    range: take(series.length, config.colors),
  });

  const handleHover = (index, pointIndex) => (e) => {
    const item = e.target.getBoundingClientRect();
    const top = item.top;
    const left = item.left + TOOLTIP_MIN_WIDTH / 2;

    if (left === tooltip.left && top === tooltip.top) {
      return;
    }

    tooltip.show({
      data: {
        index,
        pointIndex,
      },
      top: top,
      left: left,
    });
  };

  const renderSpider = ({ values, rays, webStroke, webStrokeWidth, margin, fontSize }, radius) => {
    const chartRadius = Math.abs(radius - margin);
    const circleWidth = chartRadius / values;

    const circles = times((index) => (index + 1) * circleWidth, values);
    const lines = genPoints(rays, chartRadius);
    const zeroPoint = { x: 0, y: 0 };
    const circleLabelOffset = (index) => (index + 1 === values ? -8 : 0);

    return (
      <SpiderGroup>
        {lines.map((linePoint, lineIndex) => (
          <Fragment key={lineIndex}>
            <Line
              from={zeroPoint}
              to={linePoint}
              key={lineIndex}
              stroke={UI_COLORS[6]}
              strokeWidth={webStrokeWidth}
              shapeRendering="geometricPrecision"
            />
            <text
              transform={`translate(${linePoint.xText * 1.05}, ${linePoint.yText * 1.05})`}
              y={fontSize * 0.46}
              textAnchor="middle"
              fill={UI_COLORS[3]}
              fontSize={fontSize}
            >
              {piLabels[lineIndex].indicator}
            </text>
          </Fragment>
        ))}
        {circles.map((circleRadius, circleIndex) => {
          return (
            <Fragment key={circleIndex}>
              <Circle
                r={circleRadius}
                key={circleIndex}
                stroke={UI_COLORS[6]}
                strokeWidth={webStrokeWidth}
                fill={TRANSPARENT}
              />
              <Group>
                <rect
                  height={fontSize}
                  width={20}
                  fill={'#fff'}
                  transform={`translate(-10, -${circleRadius + 5 + circleLabelOffset(circleIndex)})`}
                />
                <text
                  transform={`translate(0, -${circleRadius - fontSize / 2 + circleLabelOffset(circleIndex)})`}
                  textAnchor="middle"
                  fontSize={fontSize}
                  fill={webStroke}
                >
                  {circleIndex + 1}.0
                </text>
              </Group>
            </Fragment>
          );
        })}
      </SpiderGroup>
    );
  };

  const renderTooltipContent = () => {
    const VALUE_COLUMN_NAME = 'Score';
    const { index, pointIndex } = tooltip.data || {};
    const serie = series[index];
    const score = pathOr('-', [pointIndex, serie], data);
    const fixedScore = score !== '-' ? toFixed(score, 2) : '-';
    const { formatMessage } = useIntl();
    return (
      !isNil(index) && (
        <TooltipElementTable>
          <thead>
            <tr>
              <th>{formatMessage({ id: series[index] })}</th>
              <th>{formatMessage({ id: VALUE_COLUMN_NAME })}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <TooltipTableDataCell>
                {formatMessage({ id: piLabels[pointIndex].indicator })}:{' '}
                {formatMessage({ id: piLabels[pointIndex].label })}
              </TooltipTableDataCell>
              <TooltipTableDataCell color={color(serie)}>
                <span>{fixedScore}</span>
              </TooltipTableDataCell>
            </tr>
          </tbody>
        </TooltipElementTable>
      )
    );
  };

  const hoveredGroupIndex = pathOr(null, ['data', 'index'], tooltip);
  const hoveredPointIndex = pathOr(null, ['data', 'pointIndex'], tooltip);
  const isHoverActive = !isNil(hoveredGroupIndex);

  return (
    <Container width={width}>
      <Header title={title} subtitle={subtitle} tabs={tabs} config={config}>
        {renderLegend({ labels, colorScale: color, config })}
      </Header>
      <Chart width={width} height={height} ref={containerRef}>
        {renderOpenTooltip(always(<TooltipWithPortal>{renderTooltipContent()}</TooltipWithPortal>), tooltip)}
        <ParentSize>
          {({ width: chartWidth, height: chartHeight }) => {
            const radius = min(chartWidth, chartHeight) / 2;
            const centerY = chartHeight / 2;
            const centerX = chartWidth / 2;
            const rScale = scaleLinear({
              domain: [0, values],
              range: [0, radius - margin],
            });

            return (
              <Svg>
                <Group top={centerY} left={centerX}>
                  {radius && renderSpider(config, radius)}
                  {series.map(
                    renderSerie(
                      config,
                      axis,
                      data,
                      degreeScale,
                      rScale,
                      tooltip,
                      handleHover,
                      hoveredGroupIndex,
                      hoveredPointIndex,
                      isHoverActive
                    )
                  )}
                </Group>
              </Svg>
            );
          }}
        </ParentSize>
      </Chart>
    </Container>
  );
};
